import React from 'react';
import { ClientStatus } from './StatusBadge';

interface StatusCount {
  status: ClientStatus;
  label: string;
  icon: string;
  count: number;
}

interface StatusFilterButtonsProps {
  statusCounts: StatusCount[];
  activeFilter: ClientStatus | 'all';
  onFilterChange: (filter: ClientStatus | 'all') => void;
  totalCount: number;
  filteredCount: number;
}

/**
 * StatusFilterButtons Component
 * Phase 4: Horizontal pill buttons for filtering clients by status
 *
 * Features:
 * - Hides buttons when count = 0 (cleaner UI)
 * - Single filter active at time (radio behavior)
 * - "All" button clears filter
 * - Shows active filter visual indication + message
 */
export const StatusFilterButtons: React.FC<StatusFilterButtonsProps> = ({
  statusCounts,
  activeFilter,
  onFilterChange,
  totalCount,
  filteredCount
}) => {
  // Filter out zero-count statuses (per spec: hide when count = 0)
  const visibleStatuses = statusCounts.filter(s => s.count > 0);

  return (
    <div className="status-filter-container">
      {/* Filter Buttons */}
      <div className="filter-buttons">
        {/* "All" button - always visible */}
        <button
          className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
          onClick={() => onFilterChange('all')}
        >
          All ({totalCount})
        </button>

        {/* Status filter buttons - hide if count = 0 */}
        {visibleStatuses.map((statusInfo) => (
          <button
            key={statusInfo.status}
            className={`filter-btn ${activeFilter === statusInfo.status ? 'active' : ''}`}
            onClick={() => onFilterChange(statusInfo.status)}
          >
            <span className="filter-icon">{statusInfo.icon}</span>
            <span className="filter-label">{statusInfo.label}</span>
            <span className="filter-count">({statusInfo.count})</span>
          </button>
        ))}
      </div>

      {/* Active Filter Message */}
      {activeFilter !== 'all' && (
        <div className="filter-message">
          Showing {filteredCount} of {totalCount} clients
          {visibleStatuses.map((s) => {
            if (s.status === activeFilter) {
              return ` (filtered by ${s.icon} ${s.label})`;
            }
            return null;
          })}
        </div>
      )}

      <style>{`
        .status-filter-container {
          padding: 16px 20px;
          background: var(--card-bg);
          border-bottom: 1px solid var(--border);
        }

        .filter-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 8px;
        }

        .filter-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-secondary);
          border-radius: 20px;
          font-size: 13px;
          font-weight: 500;
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .filter-btn:hover {
          background: var(--bg-tertiary);
          border-color: var(--border-primary);
          color: var(--text-primary);
          transform: translateY(-1px);
        }

        .filter-btn.active {
          background: var(--primary);
          border-color: var(--primary);
          color: white;
          font-weight: 600;
          box-shadow: 0 2px 8px rgba(22, 163, 74, 0.3);
        }

        .filter-btn.active:hover {
          background: var(--primary-hover, #15803d);
          transform: translateY(-1px);
        }

        .filter-icon {
          font-size: 12px;
          line-height: 1;
        }

        .filter-label {
          line-height: 1;
        }

        .filter-count {
          font-size: 12px;
          font-weight: 600;
          line-height: 1;
        }

        .filter-message {
          font-size: 12px;
          color: var(--text-secondary);
          font-style: italic;
          padding-top: 8px;
        }

        /* Responsive: stack on smaller screens */
        @media (max-width: 768px) {
          .filter-buttons {
            flex-direction: column;
            align-items: stretch;
          }

          .filter-btn {
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};
